import { useState, useMemo, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Search,
  Heart,
  Briefcase,
  UserPlus,
  Link2,
  QrCode,
  Users,
  Activity,
  CreditCard,
  MessageCircle,
  Settings,
  Building2,
  ListFilter,
  UserRound,
  Database,
  Clock,
  // CalendarClock,
  ServerCog,
  ScrollText,
  HeadphonesIcon,
  LogOut,
  Lock,
  ChevronRight,
  ChevronDown,
  Globe,
  Shield,
  FileText,
  BookOpen,
  Mail,
  HelpCircle,
  MessageSquareQuote,
  Layers,
  Sparkles,
} from 'lucide-react';
import ConfirmDialog from '../common/ConfirmDialog';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logout } from '../../store/slices/authSlice';
import { getUserData, getValidAuthToken, setUserData } from '../../utils/api/auth';
import { USER_DATA_UPDATED_EVENT } from '../../utils/userDataEvents';
import { shouldShowAdminNav, shouldShowCompanyNav, getUserRole } from '../../utils/roleUtils';
import { canAccessNavPermission } from '../../utils/permissionUtils';
import { LOGIN_URL } from '../../utils/api/config';
import { fetchCompanyLogoDataUrl } from '../../utils/api/companyProfile';
import { loadCompanyProfile } from '../../utils/companyProfileCache';
import { UPGRADE_MODAL_VARIANTS, useCompanyAccess } from '../../context/CompanyAccessContext';
import { isBillingOnlyAllowedPath } from '../../utils/companyBillingGate';

/* ─── Navigation Data ─────────────────────────────────── */
const mainNav = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/' },
  { label: 'Job Search', icon: Search, path: '/job-search' },
  { label: 'Favorite Jobs', icon: Heart, path: '/favorites' },
  { label: 'Job Listings', icon: Briefcase, path: '/job-listings' },
  { label: 'Expired Jobs', icon: Clock, path: '/expired-jobs' },
  { label: 'Candidates', icon: UserPlus, path: '/candidates' },
  { label: 'Candidate Links', icon: Link2, path: '/candidate-links' },
  { label: 'Job Fair', icon: QrCode, path: '/job-fair' },
  { label: 'Company Users', icon: Users, path: '/company-users' },
  { label: 'Usage Analytics', icon: Activity, path: '/plan-usage' },
  { label: 'Plan & Billing', icon: CreditCard, path: '/billing' },
  { label: 'Support', icon: MessageCircle, path: '/support' },
  { label: 'Company Settings', icon: Settings, path: '/settings' },
];

const adminCoreNav = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard', permission: 'dashboard.view' },
  { label: 'Companies', icon: Building2, path: '/admin/companies', permission: 'companies.view' },
  { label: 'Plans', icon: ListFilter, path: '/admin/plans', permission: 'plans.view' },
  { label: 'Industries', icon: Layers, path: '/admin/industries', permission: 'industries.view' },
  { label: 'Skills Master', icon: Sparkles, path: '/admin/master-skills', permission: 'master_skills.view' },
  { label: 'Staff', icon: UserRound, path: '/admin/staff', permission: 'staff.view' },
  { label: 'Roles', icon: Shield, path: '/admin/roles', permission: 'roles.view' },
  // { label: 'Job Sources', icon: Briefcase, path: '/admin/job-sources', permission: 'job_sources.view' },
  // { label: 'Scheduled Fetch', icon: CalendarClock, path: '/admin/scheduled-fetch', permission: 'scheduled_fetch.view' },
  { label: 'Fetched Jobs', icon: Database, path: '/admin/fetched-jobs', permission: 'fetched_jobs.view' },
  { label: 'Expired Jobs', icon: Clock, path: '/admin/expired-jobs', permission: 'fetched_jobs.view' },
  { label: 'System Settings', icon: ServerCog, path: '/admin/system-settings', permission: 'system_settings.view' },
  { label: 'Accounts', icon: CreditCard, path: '/admin/accounts', permission: 'accounts.view' },
  { label: 'System Logs', icon: ScrollText, path: '/admin/system-logs', permission: 'system_logs.view' },
  { label: 'Support', icon: HeadphonesIcon, path: '/admin/support', permission: 'support.view' },
];

/** Public marketing site content — grouped under Website in the sidebar */
const adminWebsiteNav = [
  { label: 'Careers', icon: Briefcase, path: '/admin/careers', permission: 'careers_jobs.view' },
  { label: 'Blog', icon: FileText, path: '/admin/blogs', permission: 'blogs.view' },
  { label: 'Tutorials', icon: BookOpen, path: '/admin/tutorials', permission: 'tutorials.view' },
  { label: 'Features', icon: Layers, path: '/admin/features', permission: 'features.view' },
  { label: 'FAQs', icon: HelpCircle, path: '/admin/faqs', permission: 'faqs.view' },
  { label: 'Testimonials', icon: MessageSquareQuote, path: '/admin/testimonials', permission: 'testimonials.view' },
  { label: 'Terms of Service', icon: FileText, path: '/admin/terms', permission: 'terms.view' },
  { label: 'Privacy Policy', icon: ScrollText, path: '/admin/privacy', permission: 'privacy.view' },
  { label: 'Refund Policy', icon: CreditCard, path: '/admin/refund', permission: 'refund.view' },
  { label: 'Newsletter', icon: Mail, path: '/admin/newsletter', permission: 'newsletter.view' },
];

const WEBSITE_NAV_PATHS = adminWebsiteNav.map((item) => item.path);

function filterNavByPermission(items) {
  return items.filter((item) => canAccessNavPermission(item.permission));
}

function resolveSidebarDisplayName(raw) {
  if (!raw) return 'User';
  const staff = typeof raw.staff_name === 'string' ? raw.staff_name.trim() : '';
  if (staff) return staff;
  const contact = typeof raw.contact_name === 'string' ? raw.contact_name.trim() : '';
  if (contact) return contact;
  const nm = typeof raw.name === 'string' ? raw.name.trim() : '';
  if (nm) return nm;
  const cn =
    raw.company && typeof raw.company.name === 'string' ? raw.company.name.trim() : '';
  if (cn) return cn;
  return 'User';
}

/* ─── Reusable NavItem ─────────────────────────────────── */
function NavItem({ item, collapsed, restricted = false, onRestrictedClick, restrictedTitle }) {
  const Icon = item.icon;
  const location = useLocation();
  const isCandidatesRoute =
    location.pathname === '/add-candidate' || location.pathname.startsWith('/candidates/');

  const isActive =
    !restricted &&
    (item.path === '/candidates'
      ? location.pathname === '/candidates' || isCandidatesRoute
      : item.path === '/'
      ? location.pathname === '/'
      : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));

  const itemClassName = `group relative flex w-full items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 cursor-pointer select-none text-left ${
    isActive
      ? 'bg-white text-indigo-600 shadow-[0_8px_16px_-4px_rgba(99,102,241,0.25),0_4px_8px_-2px_rgba(99,102,241,0.15),inset_0_1px_0_0_rgba(255,255,255,0.9)] transform scale-[1.02] border border-indigo-100/50'
      : restricted
        ? 'text-slate-500 hover:text-slate-700 hover:bg-white/40'
        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50 hover:shadow-sm'
  }`;

  const restrictedHint = restrictedTitle || 'Upgrade your plan to access this section';

  const content = (
    <>
      <span
        className={`flex-shrink-0 transition-all duration-200 ${
          isActive ? 'text-indigo-600' : 'text-slate-500 group-hover:text-indigo-600'
        }`}
      >
        <Icon size={18} strokeWidth={isActive ? 2.5 : 2} />
      </span>

      <AnimatePresence initial={false}>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex-1 text-sm font-medium whitespace-nowrap overflow-hidden ${
              isActive ? 'text-indigo-600' : 'text-slate-700'
            }`}
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>

      {restricted && !collapsed ? (
        <Lock size={14} className="shrink-0 text-slate-400" aria-hidden />
      ) : null}

      {collapsed && (
        <span className="absolute left-full ml-3 px-3 py-2 bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700/50 text-slate-100 text-xs font-semibold rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 whitespace-nowrap z-50 shadow-xl backdrop-blur-sm">
          {restricted ? `${item.label} — ${restrictedHint}` : item.label}
        </span>
      )}
    </>
  );

  if (restricted) {
    return (
      <button
        type="button"
        onClick={onRestrictedClick}
        className={itemClassName}
        title={restrictedHint}
      >
        {content}
      </button>
    );
  }

  return (
    <NavLink to={item.path} end={item.path === '/'} className={itemClassName}>
      {content}
    </NavLink>
  );
}

/* ─── Nested nav link (Website submenu) ───────────────── */
function NavSubItem({ item, collapsed }) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.path}
      className={({ isActive }) =>
        `group relative flex items-center gap-2.5 pl-9 pr-3 py-2 rounded-lg transition-all duration-200 cursor-pointer select-none ${isActive
          ? 'bg-indigo-50 text-indigo-600 font-medium'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex-shrink-0 ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-indigo-500'}`}
          >
            <Icon size={16} strokeWidth={isActive ? 2.5 : 2} />
          </span>
          {!collapsed && (
            <span className={`text-[13px] whitespace-nowrap overflow-hidden ${isActive ? 'text-indigo-600' : 'text-slate-600'}`}>
              {item.label}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/* ─── Website dropdown (public site admin) ─────────────── */
function WebsiteNavDropdown({ items, collapsed, isWebsiteRoute }) {
  const [open, setOpen] = useState(isWebsiteRoute);
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  useEffect(() => {
    if (isWebsiteRoute) setOpen(true);
  }, [isWebsiteRoute]);

  if (!items.length) return null;

  const parentActive = isWebsiteRoute;

  if (collapsed) {
    return (
      <div
        className="relative"
        onMouseEnter={() => setFlyoutOpen(true)}
        onMouseLeave={() => setFlyoutOpen(false)}
      >
        <button
          type="button"
          className={`group relative flex w-full items-center justify-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 cursor-pointer select-none ${parentActive
            ? 'bg-white text-indigo-600 shadow-[0_8px_16px_-4px_rgba(99,102,241,0.25),0_4px_8px_-2px_rgba(99,102,241,0.15),inset_0_1px_0_0_rgba(255,255,255,0.9)] border border-indigo-100/50'
            : 'text-slate-600 hover:text-slate-900 hover:bg-white/50 hover:shadow-sm'
            }`}
          aria-expanded={flyoutOpen}
          aria-haspopup="true"
        >
          <Globe
            size={18}
            strokeWidth={parentActive ? 2.5 : 2}
            className={parentActive ? 'text-indigo-600' : 'text-slate-500 group-hover:text-indigo-600'}
          />
          <span className="absolute left-full ml-3 px-3 py-2 bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700/50 text-slate-100 text-xs font-semibold rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 whitespace-nowrap z-50 shadow-xl">
            Website
          </span>
        </button>

        <AnimatePresence>
          {flyoutOpen && (
            <motion.div
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -6 }}
              transition={{ duration: 0.15 }}
              className="absolute left-full top-0 ml-2 min-w-[200px] py-2 px-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50"
            >
              <p className="px-3 py-1.5 text-[10px] font-bold tracking-widest uppercase text-slate-400">
                Website
              </p>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${isActive
                        ? 'bg-indigo-50 text-indigo-600 font-medium'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`
                    }
                    onClick={() => setFlyoutOpen(false)}
                  >
                    {({ isActive }) => {
                      const Icon = item.icon;
                      return (
                        <>
                          <Icon size={15} className={isActive ? 'text-indigo-600' : 'text-slate-400'} />
                          <span>{item.label}</span>
                        </>
                      );
                    }}
                  </NavLink>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`group relative flex w-full items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 cursor-pointer select-none ${parentActive
          ? 'bg-white text-indigo-600 shadow-[0_8px_16px_-4px_rgba(99,102,241,0.25),0_4px_8px_-2px_rgba(99,102,241,0.15),inset_0_1px_0_0_rgba(255,255,255,0.9)] border border-indigo-100/50'
          : 'text-slate-600 hover:text-slate-900 hover:bg-white/50 hover:shadow-sm'
          }`}
        aria-expanded={open}
      >
        <Globe
          size={18}
          strokeWidth={parentActive ? 2.5 : 2}
          className={`flex-shrink-0 ${parentActive ? 'text-indigo-600' : 'text-slate-500 group-hover:text-indigo-600'}`}
        />
        <span className={`flex-1 text-left text-sm font-medium ${parentActive ? 'text-indigo-600' : 'text-slate-700'}`}>
          Website
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className={`flex-shrink-0 ${parentActive ? 'text-indigo-500' : 'text-slate-400'}`}
        >
          <ChevronDown size={16} strokeWidth={2.5} />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-0.5 pb-1">
              {items.map((item) => (
                <NavSubItem key={item.path} item={item} collapsed={collapsed} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Section Label ────────────────────────────────────── */
function SectionLabel({ label, collapsed }) {
  return (
    <div className="px-3 mb-2 mt-5">
      <AnimatePresence initial={false}>
        {!collapsed ? (
          <motion.p
            key="label"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="text-[10px] font-bold tracking-widest uppercase text-slate-500/70"
          >
            {label}
          </motion.p>
        ) : (
          <motion.div
            key="divider"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent rounded-full"
          />
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Main Sidebar ─────────────────────────────────────── */
export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [userData, setUserDataState] = useState(null);
  const [companyLogoSrc, setCompanyLogoSrc] = useState('');
  const [companyLogoBroken, setCompanyLogoBroken] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();
  
  // Get user from Redux store
  const reduxUser = useAppSelector((state) => state.auth?.user);
  
  // Get user role for navigation visibility
  const userRole = getUserRole();
  const showAdminNav = shouldShowAdminNav();
  const showCompanyNav = shouldShowCompanyNav();
  const isCompanyStaffUser = userRole === 'company_staff';
  const { isBillingOnly, openUpgradeModal } = useCompanyAccess();
  const companyNavItems = useMemo(
    () =>
      mainNav.filter((item) => {
        // Staff never manage billing or usage — hide those links in all access modes.
        if (isCompanyStaffUser) {
          return item.path !== '/plan-usage' && item.path !== '/billing';
        }
        return true;
      }),
    [isCompanyStaffUser]
  );

  const handleRestrictedNavClick = () => {
    openUpgradeModal(UPGRADE_MODAL_VARIANTS.NAVIGATION);
  };

  const isNavItemRestricted = (path) =>
    isBillingOnly && !isBillingOnlyAllowedPath(path, { canAccessBilling: !isCompanyStaffUser });
  
  // Check URL parameters for user data (from login redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const name = urlParams.get('name');
    const role = urlParams.get('role');
    const email = urlParams.get('email');
    const status = urlParams.get('status');
    
    if (name) {
      // User data passed via URL, store it
      const userFromUrl = {
        name: name,
        role: role || status || 'User',
        email: email || null,
        status: status || null,
      };
      
      setUserData(userFromUrl);
      setUserDataState(userFromUrl);
      
      // Clear URL parameters
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);
  
  // Load user data from localStorage on mount and when storage changes
  useEffect(() => {
    const loadUserData = () => {
      // Check if token is valid first
      const token = getValidAuthToken();
      if (!token) {
        setUserDataState(null);
        return;
      }
      
      const storedUser = getUserData();
      setUserDataState(storedUser);
    };
    
    // Load initially
    loadUserData();
    
    // Listen for storage changes (when data is updated from another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === 'user_data' || e.key === 'userData' || e.key === 'auth_token' || e.key === 'authToken') {
        loadUserData();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(USER_DATA_UPDATED_EVENT, loadUserData);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        loadUserData();
      }
    });
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(USER_DATA_UPDATED_EVENT, loadUserData);
    };
  }, []);
  
  // Helper function to get user initials
  const getUserInitials = (name) => {
    if (!name) return 'GU';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Helper function to format role display
  const formatRole = (role, status) => {
    // If role exists and is not a status, use it
    if (role && !['Trial', 'Active', 'Inactive'].includes(role)) {
      return role.charAt(0).toUpperCase() + role.slice(1);
    }
    // If status exists, format it nicely
    if (status) {
      if (status === 'Trial') return 'Trial Account';
      if (status === 'Active') return 'Active Account';
      if (status === 'Inactive') return 'Inactive Account';
      return status;
    }
    // Default
    return 'User';
  };

  // Get user data from Redux, localStorage state, or localStorage directly
  const user = useMemo(() => {
    // Try Redux store first
    if (reduxUser) {
      const userName = resolveSidebarDisplayName(reduxUser);
      const userRole = formatRole(reduxUser.role, reduxUser.status || reduxUser.company?.status);
      const initials = getUserInitials(userName);
      return { name: userName, role: userRole, initials };
    }
    
    // Try localStorage state (from useEffect)
    if (userData) {
      const userName = resolveSidebarDisplayName(userData);
      const userRole = formatRole(userData.role, userData.status || userData.company?.status);
      const initials = getUserInitials(userName);
      return { name: userName, role: userRole, initials };
    }
    
    // Fallback to direct localStorage read
    const storedUser = getUserData();
    if (storedUser) {
      const userName = resolveSidebarDisplayName(storedUser);
      const userRole = formatRole(storedUser.role, storedUser.status || storedUser.company?.status);
      const initials = getUserInitials(userName);
      return { name: userName, role: userRole, initials };
    }
    
    // Default fallback
    return { name: 'Guest User', role: 'Guest', initials: 'GU' };
  }, [reduxUser, userData]);

  useEffect(() => {
    if (!showCompanyNav) {
      setCompanyLogoSrc('');
      setCompanyLogoBroken(false);
      return undefined;
    }

    let cancelled = false;

    const loadCompanyLogo = async (force = false) => {
      try {
        const profile = await loadCompanyProfile(force);
        if (cancelled) return;

        const logoUrl = String(profile?.logo_url || '').trim();
        if (!logoUrl) {
          setCompanyLogoSrc('');
          setCompanyLogoBroken(false);
          return;
        }

        try {
          const dataUrl = await fetchCompanyLogoDataUrl();
          if (!cancelled) {
            setCompanyLogoSrc(dataUrl || logoUrl);
            setCompanyLogoBroken(false);
          }
        } catch {
          if (!cancelled) {
            setCompanyLogoSrc(logoUrl);
            setCompanyLogoBroken(false);
          }
        }
      } catch {
        if (!cancelled) {
          setCompanyLogoSrc('');
        }
      }
    };

    loadCompanyLogo();

    const handleProfileUpdated = () => {
      loadCompanyLogo(true);
    };

    window.addEventListener(USER_DATA_UPDATED_EVENT, handleProfileUpdated);

    return () => {
      cancelled = true;
      window.removeEventListener(USER_DATA_UPDATED_EVENT, handleProfileUpdated);
    };
  }, [showCompanyNav]);

  const showCompanyLogo = showCompanyNav && companyLogoSrc && !companyLogoBroken;

  const accessibleCoreNav = useMemo(
    () => filterNavByPermission(adminCoreNav),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- permissions from storage
    [reduxUser, userData]
  );

  const accessibleWebsiteNav = useMemo(
    () => filterNavByPermission(adminWebsiteNav),
    [reduxUser, userData]
  );

  const isWebsiteRoute = WEBSITE_NAV_PATHS.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      // Redirect to login page (main app)
      window.location.href = LOGIN_URL;
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, clear local storage and redirect
      localStorage.clear();
      window.location.href = LOGIN_URL;
    }
  };

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 72 : 260 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="relative flex flex-col h-screen flex-shrink-0 overflow-hidden bg-white border-r border-slate-200 shadow-sm"
    >

      {/* ── Logo ── */}
      <div className={`flex items-center border-b border-slate-200 ${collapsed ? 'flex-col gap-2.5 px-2 pt-5 pb-4' : 'gap-2.5 px-4 pt-5 pb-5'}`}>
        <img
          src="/favicon.svg"
          alt="JobScribe"
          className="w-9 h-9 flex-shrink-0 rounded-[10px] shadow-lg shadow-indigo-500/25"
          draggable={false}
        />
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden flex-1"
            >
              <span className="text-lg font-bold text-slate-900 tracking-tight whitespace-nowrap">
                Job<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-violet-600">Scribe</span>
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(c => !c)}
          className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all duration-200"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <motion.span
            animate={{ rotate: collapsed ? 0 : 180 }}
            transition={{ duration: 0.25 }}
            className="flex items-center justify-center"
          >
            <ChevronRight size={16} strokeWidth={2.5} />
          </motion.span>
        </button>
      </div>

      {/* ── Scrollable Nav ── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 custom-scrollbar">
        {/* Company Navigation - Only show for company users */}
        {showCompanyNav && (
          <>
            {/* Main section */}
            <div className="space-y-1">
              {companyNavItems.map((item) => (
                <NavItem
                  key={item.path}
                  item={item}
                  collapsed={collapsed}
                  restricted={isNavItemRestricted(item.path)}
                  onRestrictedClick={handleRestrictedNavClick}
                  restrictedTitle={
                    isCompanyStaffUser
                      ? 'Ask your company admin to renew access'
                      : 'Upgrade your plan to access this section'
                  }
                />
              ))}
            </div>
          </>
        )}

        {/* Admin Navigation - Only show for platform admins */}
        {showAdminNav && (
          <>
            <SectionLabel collapsed={collapsed} />
            <div className="space-y-1">
              {accessibleCoreNav.map((item) => (
                <NavItem key={item.path} item={item} collapsed={collapsed} />
              ))}
              <WebsiteNavDropdown
                items={accessibleWebsiteNav}
                collapsed={collapsed}
                isWebsiteRoute={isWebsiteRoute}
              />
            </div>
          </>
        )}

        {/* Show message if no navigation available */}
        {!showAdminNav && !showCompanyNav && (
          <div className="px-3 py-4 text-center">
            <p className="text-sm text-slate-500">
              No navigation available for your role.
            </p>
          </div>
        )}
      </nav>

      {/* ── User Footer ── */}
      <div className="border-t border-slate-200 p-3">
        <div
          className={`relative flex items-center gap-3 rounded-xl p-2.5 transition-all duration-200 hover:bg-white/80 hover:shadow-sm group ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          {/* Avatar — company logo when uploaded, otherwise user initials */}
          <div className="relative flex-shrink-0">
            {showCompanyLogo ? (
              <img
                src={companyLogoSrc}
                alt="Company logo"
                className="h-9 w-9 rounded-xl border border-slate-200 bg-white object-contain p-0.5 shadow-lg shadow-indigo-500/30 ring-2 ring-white/50"
                onError={() => setCompanyLogoBroken(true)}
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-xs font-bold text-white shadow-lg shadow-indigo-500/30 ring-2 ring-white/50">
                {user.initials}
              </div>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-400 shadow-sm" />
          </div>

          {/* Name & role */}
          <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.18 }}
                className="min-w-0 flex-1 overflow-hidden"
              >
                <p className="truncate text-sm font-bold leading-tight text-slate-900">{user.name}</p>
                <p className="mt-0.5 text-[11px] font-semibold leading-tight text-slate-600">{user.role}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Logout */}
          <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="group/logout relative flex-shrink-0"
              >
                <button
                  type="button"
                  onClick={() => setShowLogoutConfirm(true)}
                  title="Sign out"
                  aria-label="Sign out of your account"
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-all duration-200 hover:bg-rose-50 hover:text-rose-500"
                >
                  <LogOut size={16} strokeWidth={2.5} />
                </button>
                <span className="pointer-events-none absolute bottom-full right-0 z-50 mb-2 whitespace-nowrap rounded-lg border border-slate-700/50 bg-gradient-to-r from-slate-800 to-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 opacity-0 shadow-xl backdrop-blur-sm transition-opacity duration-150 group-hover/logout:opacity-100">
                  Sign out securely
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Logout button when collapsed */}
          {collapsed && (
            <div className="group/logout absolute bottom-3 left-1/2 -translate-x-1/2">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(true)}
                title="Sign out"
                aria-label="Sign out of your account"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 transition-all duration-200 hover:bg-rose-50 hover:text-rose-500"
              >
                <LogOut size={16} strokeWidth={2.5} />
              </button>
              <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-3 -translate-y-1/2 whitespace-nowrap rounded-lg border border-slate-700/50 bg-gradient-to-r from-slate-800 to-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 opacity-0 shadow-xl backdrop-blur-sm transition-opacity duration-150 group-hover/logout:opacity-100">
                Sign out securely
              </span>
            </div>
          )}

          {/* Tooltip when collapsed */}
          {collapsed && (
            <span className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-lg border border-slate-700/50 bg-gradient-to-r from-slate-800 to-slate-900 px-3 py-2 text-xs font-semibold text-slate-100 opacity-0 shadow-xl backdrop-blur-sm transition-opacity duration-150 group-hover:opacity-100">
              {user.name} · {user.role}
            </span>
          )}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Sign out of JobScribe?"
        message="You will be signed out of this session and will need to log in again to access your dashboard."
        confirmText="Yes, Sign Out"
        cancelText="Stay Logged In"
        variant="warning"
      />
    </motion.aside>
  );
}
